import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const formatMoney = (amount, currency = 'GHS') => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toMediaUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL}${url}`;
};

function parseJsonSafely(text) {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

const paymentStatusColors = {
  paid: { bg: '#d1e7dd', text: '#0f5132' },
  pending: { bg: '#fff3cd', text: '#664d03' },
  failed: { bg: '#f8d7da', text: '#842029' },
};

const orderStatusColors = {
  processing: { bg: '#dbeafe', text: '#1e3a8a' },
  pending_payment: { bg: '#f3e8ff', text: '#6b21a8' },
  payment_failed: { bg: '#fee2e2', text: '#991b1b' },
};

function StatusBadge({ value, palette }) {
  const style = palette[value] || { bg: '#e5e7eb', text: '#111827' };
  return (
    <span style={{
      background: style.bg,
      color: style.text,
      borderRadius: 999,
      padding: '4px 10px',
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'capitalize',
    }}>
      {String(value || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function OrderDetailsPage({ products = [] }) {
  const navigate = useNavigate();
  const { orderNumber } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError('');

      try {
        const rawSession = localStorage.getItem('tk_fashion_user_session');
        const session = rawSession ? JSON.parse(rawSession) : null;
        const token = session?.token;

        if (!token) {
          throw new Error('Please log in to view order details.');
        }

        const response = await fetch(`${API_BASE_URL}/api/orders/my/${encodeURIComponent(orderNumber || '')}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const text = await response.text();
        const payload = parseJsonSafely(text);

        if (!response.ok) {
          throw new Error(payload?.message || 'Unable to load order details.');
        }

        setOrder(payload?.data || null);
      } catch (fetchError) {
        setError(fetchError.message || 'Unable to load order details.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderNumber]);

  const itemsWithMedia = useMemo(() => {
    if (!order?.items) return [];

    return order.items.map((item) => {
      const match = products.find((product) => String(product?._id) === String(item?.productId));
      const images = Array.isArray(match?.images) ? match.images.map(toMediaUrl).filter(Boolean) : [];
      const videos = Array.isArray(match?.videos) ? match.videos.map(toMediaUrl).filter(Boolean) : [];

      return {
        ...item,
        images,
        videos,
      };
    });
  }, [order, products]);

  if (loading) {
    return <section className="cart-page"><div className="cart-summary-card">Loading order details...</div></section>;
  }

  if (error) {
    return (
      <section className="cart-page">
        <div className="cart-summary-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <p style={{ margin: 0 }}>{error}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="checkout-button" onClick={() => navigate('/orders')}>
              Back to My Orders
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!order) {
    return (
      <section className="cart-page">
        <div className="cart-summary-card">
          <p style={{ margin: 0 }}>Order not found.</p>
          <button type="button" className="checkout-button" style={{ marginTop: 12 }} onClick={() => navigate('/orders')}>
            Back to My Orders
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="cart-page" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0 }}>Order #{order.orderNumber}</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>Placed {formatDate(order.createdAt)}</p>
        </div>
        <button type="button" className="checkout-button" onClick={() => navigate('/orders')}>
          Back to My Orders
        </button>
      </div>

      <div className="cart-summary-card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <StatusBadge value={order.paymentStatus} palette={paymentStatusColors} />
          <StatusBadge value={order.orderStatus} palette={orderStatusColors} />
        </div>

        <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
          <p style={{ margin: 0 }}><strong>Paid At:</strong> {formatDate(order.paidAt)}</p>
          <p style={{ margin: 0 }}><strong>Fulfillment:</strong> {order.fulfillment?.method || 'N/A'}</p>
          <p style={{ margin: 0 }}><strong>Recipient:</strong> {order.fulfillment?.recipientName || 'N/A'} ({order.fulfillment?.recipientPhone || 'N/A'})</p>
          <p style={{ margin: 0 }}><strong>Address:</strong> {order.fulfillment?.deliveryAddress || order.fulfillment?.pickupAddress || 'N/A'}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 14 }}>
        {itemsWithMedia.map((item, index) => (
          <article key={`${item.productId}-${index}`} className="cart-summary-card">
            <div className="cart-summary-line" style={{ marginBottom: 10 }}>
              <strong>{item.name || 'Item'} x {item.quantity || 0}</strong>
              <strong>{formatMoney((item.unitPrice || 0) * (item.quantity || 0), order.totals?.currency)}</strong>
            </div>
            <p style={{ margin: '0 0 10px', color: '#64748b' }}>
              Delivery Option: {item.selectedDelivery || 'standard'}
            </p>

            {item.images.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <h4 style={{ margin: '0 0 8px' }}>Images</h4>
                <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6 }}>
                  {item.images.map((img, i) => (
                    <img
                      key={`${img}-${i}`}
                      src={img}
                      alt={`${item.name || 'Item'} ${i + 1}`}
                      style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 10, border: '1px solid #dbe4f1' }}
                    />
                  ))}
                </div>
              </div>
            )}

            {item.videos.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 8px' }}>Videos</h4>
                <div style={{ display: 'grid', gap: 10 }}>
                  {item.videos.map((video, i) => (
                    <video
                      key={`${video}-${i}`}
                      controls
                      preload="metadata"
                      style={{ width: '100%', maxWidth: 420, borderRadius: 10, border: '1px solid #dbe4f1', background: '#0f172a' }}
                    >
                      <source src={video} />
                    </video>
                  ))}
                </div>
              </div>
            )}

            {item.images.length === 0 && item.videos.length === 0 && (
              <p style={{ margin: 0, color: '#64748b' }}>No media available for this item.</p>
            )}
          </article>
        ))}
      </div>

      <div className="cart-summary-card" style={{ marginTop: 14, display: 'grid', gap: 6 }}>
        <div className="cart-summary-line"><span>Subtotal</span><strong>{formatMoney(order.totals?.subtotal, order.totals?.currency)}</strong></div>
        <div className="cart-summary-line"><span>Express Surcharge</span><strong>{formatMoney(order.totals?.expressSurcharge, order.totals?.currency)}</strong></div>
        <div className="cart-summary-line"><span>Delivery Fee</span><strong>{formatMoney(order.totals?.deliveryFee, order.totals?.currency)}</strong></div>
        <div className="cart-summary-line"><span>Total</span><strong>{formatMoney(order.totals?.total, order.totals?.currency)}</strong></div>
      </div>
    </section>
  );
}
